import { useGetChoices } from "@features/ax/assetRegistry/systems/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useFacilityTypes } from "../../hooks";
import { FilterState } from "../../types";

interface FacilityFilterProps {
  currentFilters?: FilterState & { search: string };
  onApplyFilters: (filters: any) => void;
  onResetFilters: () => void;
}

export const FacilityFilter: React.FC<FacilityFilterProps> = ({
  currentFilters = {
    search: "",
    facilityType: "",
    status: null,
  },
  onApplyFilters,
  onResetFilters,
}) => {
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    facilityType: string;
    status: string;
  }>({
    facilityType: currentFilters?.facilityType || "all",
    status:
      currentFilters?.status !== undefined && currentFilters?.status !== null
        ? String(currentFilters.status)
        : "all",
  });

  const { data: statusData } = useGetChoices("facility_status");
  const { data: facilityTypesData } = useFacilityTypes();

  const facilityTypeOptions = useMemo(() => {
    const options = [{ label: "All Types", value: "all" }];
    if (facilityTypesData?.result) {
      options.push(
        ...facilityTypesData.result.map((item: any) => ({
          value: item.code,
          label: item.name,
        }))
      );
    }
    return options;
  }, [facilityTypesData]);

  const statusOptions = useMemo(() => {
    const options = [{ label: "All Statuses", value: "all" }];
    if (statusData) {
      options.push(
        ...statusData.map((status) => ({
          label: status.value,
          value: String(status.key),
        }))
      );
    }
    return options;
  }, [statusData]);

  // Create form fields
  const createFormFields = (): FormField[] => [
    {
      name: "facilityType",
      type: "select",
      label: "Facility Type",
      required: false,
      options: facilityTypeOptions,
      placeholder: "Select facility type",
      classes: FIELD_CLASSES,
    },
    {
      name: "status",
      type: "select",
      label: "Status",
      required: false,
      options: statusOptions,
      placeholder: "Select status",
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: tempFilters,
  });

  // Update form values when current filters change
  useEffect(() => {
    const newTempFilters = {
      facilityType: currentFilters?.facilityType || "all",
      status: String(currentFilters?.status ?? "all"),
    };

    setTempFilters(newTempFilters);
    form.reset(newTempFilters);
  }, [currentFilters, form]);

  // Handle form submission - format data for the parent component
  const handleSubmit = (data: any) => {
    const filtersToApply: any = {
      facilityType:
        data.facilityType && data.facilityType !== "all"
          ? data.facilityType
          : "",
      status: data.status && data.status !== "all" ? String(data.status) : "",
    };

    onApplyFilters(filtersToApply);
  };

  // Handle reset
  const handleReset = () => {
    const defaultFilters = {
      facilityType: "all",
      status: "all",
    };
    setTempFilters(defaultFilters);
    form.reset(defaultFilters);
    onResetFilters();
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-3 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onFieldChange={handleFieldChange}
      config={{
        showSubmitButton: false, // We're using custom footer
        showResetButton: false, // We're using custom footer
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={2}
      responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
      className="space-y-6"
      renderCustomFooter={renderCustomFooter}
    />
  );
};
